/* « Valider un cadeau », the door (gift-wallet review B/C and P0.3, merchant-app review MA-3, errata B1, Version 19).

   The till is the player's page ({player}/caisse, Technical's): it validates, with each member's code personnel. What it
   cannot do is register itself, since that takes the merchant's session. So the door opens the till on this device,
   unless this device needs registering and the person can do it here in one tap: the owner, an admin, or a manager in
   scope (POST /v1/till/devices, contract 08 §8.4; the API's 403 forbidden_role is the real guard). The till then opens
   with its token (`#device=`, which the till page moves into its own storage and scrubs from the address bar), in this
   tab: on a counter device the till is the job, and a tab opened after the POST would be blocked by Safari. The door
   always replaces itself in the history, so « Retour » from the till never lands back on it.

   « This device » is the till this browser registered here (its id, `bmr.caisse.<world>`). Only an answer forgets it: a
   read of the tills that lists it revoked or absent. A failed read (offline, 5xx) or a refused one (403) keeps it, and the
   door opens the till, which says itself what is wrong. A device made a till from a code has no such mark, so the card
   opens the till too. `?enregistrer=1` always shows the card to someone who can register, mark or not: the way back when
   the till lost its token while the server still lists the device (cleared data, Safari, another browser). For another
   device without an account: the code of the till, shown once (V19's last line), which the till page takes pasted, or
   its link opened there. The card names one establishment, the scope's, else the first in scope (V1 has one; picking
   among several is the multi-location backlog). The list of tills is Équipe's « Vos caisses ».

   A refused write says the app's words (C-2: `tillRefusal`, never the API's `message`), and one tap makes one till: Enter
   in a name field does nothing while a POST is on its way.

   `?mode=test` (MA-8's « Ouvrir la caisse en mode test ») carries the test mode to the till (`#mode=test`, 08 §8.2b).
   The demo (no API) has no till on the player host to open (a mock token means nothing there): it opens the demo till. */
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Button, Field, Input, Modal, Note, PageHeader, Skeleton, useToast } from '@/ui'
import { ApiError, apiWrites, playerOrigin, useLocations } from '@/lib/api'
import { refusalLines } from '@/lib/campaign-api'
import { ALL, allowedLocations, currentUser, useScope, type Role } from '@/lib/scope'
import { currentWorld } from '@/mocks/worlds'
import { tillLink, useCreateTillDevice, useTillDevices, type NewTillDevice } from '@/routes/parametres/api'

const DEFAULT_NAME = 'Caisse — comptoir'
/** The role, mid-sentence: « Vous êtes connecté en propriétaire » (lower case, not ROLE_LABEL's label). */
const ROLE_WORD: Record<Role, string> = { owner: 'propriétaire', admin: 'administrateur', manager: 'manager', staff: 'collaborateur' }
const markKey = () => `bmr.caisse.${currentWorld()}`
/** The till this browser registered at the door, if any (its id). */
export const tillMark = (): string | null => { try { return localStorage.getItem(markKey()) } catch { return null } }
export const setTillMark = (id: string | null) => {
  try { if (id) localStorage.setItem(markKey(), id); else localStorage.removeItem(markKey()) } catch { /* fine */ }
}

/** Why a till write was refused, in the app's words (C-2, `memberRefusal`'s shape): the API's own `message` is never
    shown, nor the browser's (« Failed to fetch ») or a gateway's. */
export function tillRefusal(e: unknown): string {
  if (!(e instanceof ApiError) || e.status === 401) return refusalLines(e)[0]
  switch (e.code) {
    case 'forbidden_role': return 'Seuls le propriétaire, les administrateurs et les managers gèrent les caisses.'
    case 'not_found': return e.field === 'locationId'
      ? 'Cet établissement n’existe pas, ou plus, sur le serveur. Rechargez la page.'
      : 'Cette caisse n’existe pas, ou plus, sur le serveur. Rechargez la page.'
    case 'invalid_name': return 'Donnez un nom à la caisse (60 caractères au plus).'
  }
  return 'L’action n’a pas abouti\u00a0: rien n’a changé. Réessayez dans un instant.'
}

/** The till, on this device: the player's page with an API, the demo till without one. Always in place of the door. */
function useOpenTill() {
  const nav = useNavigate()
  return (o: { token?: string; test?: boolean } = {}) => {
    if (!apiWrites) { nav(`/till${o.token || tillMark() ? '?ecran=identity' : ''}`, { replace: true }); return }
    const hash = [o.token ? tillLink(o.token).split('#')[1] : '', o.test ? 'mode=test' : ''].filter(Boolean).join('&')
    window.location.replace(`${playerOrigin()}/caisse${hash ? `#${hash}` : ''}`)
  }
}

export default function Caisse() {
  const [params] = useSearchParams()
  const test = params.get('mode') === 'test'
  const force = params.get('enregistrer') === '1'
  const toast = useToast()
  const [scope] = useScope()
  const open = useOpenTill()
  const user = currentUser()
  const { data: places, isPending: placesPending } = useLocations()
  const { data: tills, isPending: tillsPending, isSuccess: tillsRead } = useTillDevices(user.role !== 'staff')
  const create = useCreateTillDevice()

  const mine = allowedLocations(places ?? [])
  const canRegister = user.role !== 'staff' && mine.length > 0
  const at = (scope !== ALL && mine.some(l => l.id === scope) ? scope : mine[0]?.id) ?? ''
  const atName = mine.find(l => l.id === at)?.name ?? ''

  const [name, setName] = useState(DEFAULT_NAME)
  const [other, setOther] = useState<string | null>(null)
  const [fresh, setFresh] = useState<NewTillDevice | null>(null)
  /* once the till is opening, the page decides nothing more (a list read before the new till would forget its mark) */
  const leaving = useRef(false)
  const leave = (o: { token?: string; test?: boolean }) => { leaving.current = true; open(o) }
  /* one tap, one till: the buttons are disabled while the POST is on its way, but a field's Enter is not a button (and
     two presses can land before the next render) */
  const sending = useRef(false)
  const send = (body: { name: string; locationId: string }, onSuccess: (d: NewTillDevice) => void) => {
    if (sending.current || create.isPending) return
    sending.current = true
    create.mutate(body, {
      onSuccess,
      onError: e => toast({ message: tillRefusal(e) }),
      onSettled: () => { sending.current = false },
    })
  }

  /* the door's decision: the till, unless this device needs registering and the person can do it here */
  const mark = tillMark()
  const gone = !!mark && tillsRead && !(tills ?? []).some(t => t.id === mark && !t.revokedAt)
  const decided = !placesPending && (!canRegister || force || !mark || !tillsPending)
  const toTill = decided && (!canRegister || (!force && !!mark && !gone))
  useEffect(() => { if (gone && !leaving.current) setTillMark(null) }, [gone])
  useEffect(() => { if (toTill && !leaving.current) leave({ test }) }, [toTill]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!decided || toTill || leaving.current) return (
    <div style={{ display: 'grid', gap: 10, maxWidth: 640 }}><Skeleton width="42%" /><Skeleton /><Skeleton width="65%" /></div>
  )

  const register = () => {
    const n = name.trim()
    if (!n) return
    send({ name: n, locationId: at }, d => { leaving.current = true; setTillMark(d.id); leave({ token: d.deviceToken, test }) })
  }
  const makeCode = () => {
    const n = (other ?? '').trim()
    if (!n) return
    send({ name: n, locationId: at }, d => { setOther(null); setFresh(d) })
  }
  const copy = async (text: string, done: string, what: string) => {
    try { await navigator.clipboard.writeText(text); toast({ message: done }) }
    catch { toast({ message: `Copie impossible ici : sélectionnez ${what} et copiez-le.` }) }
  }
  const link = fresh ? tillLink(fresh.deviceToken) : ''

  return (
    <section data-frame="CA-02" className="page" data-testid="porte-caisse" style={{ maxWidth: 640 }}>
      <PageHeader title="Enregistrer cette caisse" />
      <p className="t-body" style={{ margin: 0 }}>
        Cet appareil devient une caisse de {atName}. Vous êtes connecté en {ROLE_WORD[user.role]}&nbsp;: un tap suffit.
      </p>
      <div style={{ display: 'grid', gap: 12 }}>
        <Field label="Nom de la caisse">{id => (
          <Input id={id} value={name} maxLength={60} autoFocus onChange={e => setName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') register() }} />
        )}</Field>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <Button variant="primary" disabled={!name.trim() || create.isPending} onClick={register}>Enregistrer cet appareil</Button>
          <Button variant="quiet" onClick={() => leave({ test })}>Ouvrir la caisse</Button>
        </div>
      </div>
      <p className="f-help" style={{ margin: 0 }}>
        Sur un autre appareil, sans compte&nbsp;? Ouvrez «&nbsp;Valider un cadeau&nbsp;» et collez le code de la caisse, affiché une seule fois ici.
      </p>
      <div><Button size="sm" variant="quiet" onClick={() => setOther(DEFAULT_NAME)}>Créer le code de la caisse</Button></div>

      {/* another device: its name first, then the code, shown once */}
      <Modal open={other !== null} title="Code de la caisse" onClose={() => setOther(null)}
        actions={<>
          <Button size="sm" variant="quiet" onClick={() => setOther(null)}>Annuler</Button>
          <Button size="sm" variant="primary" disabled={!other?.trim() || create.isPending} onClick={makeCode}>Créer le code</Button>
        </>}>
        <Field label="Nom de la caisse">{id => (
          <Input id={id} value={other ?? ''} maxLength={60} autoFocus onChange={e => setOther(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') makeCode() }} />
        )}</Field>
      </Modal>
      <Modal open={fresh !== null} title="Code de la caisse" onClose={() => setFresh(null)}
        actions={<Button size="sm" variant="primary" onClick={() => setFresh(null)}>Fermer</Button>}>
        <p className="t-body">Sur l’appareil de «&nbsp;{fresh?.name}&nbsp;», ouvrez {playerOrigin().replace(/^https:\/\//, '')}/caisse et collez ce code.</p>
        <code data-till-code style={{ display: 'block', overflowWrap: 'anywhere', padding: '6px 0' }}>{fresh?.deviceToken}</code>
        <div><Button size="sm" onClick={() => void copy(fresh?.deviceToken ?? '', 'Code copié.', 'le code')}>Copier le code</Button></div>
        <p className="t-body">Ou ouvrez ce lien sur cet appareil-là&nbsp;: il colle le code pour vous.</p>
        <code data-till-link style={{ display: 'block', overflowWrap: 'anywhere', padding: '6px 0' }}>{link}</code>
        <div><Button size="sm" variant="quiet" onClick={() => void copy(link, 'Lien copié. Ouvrez-le sur l’appareil de caisse.', 'le lien')}>Copier le lien</Button></div>
        <Note tone="candour">Ce code ne s’affichera plus. Envoyez-le seulement à l’appareil de caisse&nbsp;: qui le colle enregistre son appareil comme caisse.</Note>
      </Modal>
    </section>
  )
}
